/**
 * CreateBankDialog — replaces window.prompt() for bank creation.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Loader2 } from 'lucide-react';

export default function CreateBankDialog({ open, onClose, onCreated }) {
  const { post } = useApi();
  const [form, setForm] = useState({
    title: '',
    description: '',
    default_language: 'en',
    client_id: '',
    tags: '',
  });
  const [saving, setSaving] = useState(false);

  const update = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const canSave = form.title.trim().length > 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const tagsArr = form.tags
        .split(',').map(t => t.trim()).filter(Boolean).slice(0, 20);
      const payload = {
        title: form.title.trim(),
        default_language: form.default_language,
        tags: tagsArr,
      };
      if (form.description.trim()) payload.description = form.description.trim();
      if (form.client_id.trim()) payload.client_id = form.client_id.trim();
      const created = await post('/question-banks', payload);
      toast.success('Bank created');
      onCreated?.(created);
      // Reset for next time
      setForm({ title: '', description: '', default_language: 'en', client_id: '', tags: '' });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-bank-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">New question bank</DialogTitle>
          <DialogDescription>
            Banks group reusable, version-pinned questions that quizzes can sample from.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="cb-title">Title *</Label>
            <Input
              id="cb-title"
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="Tier-1 customer service · onboarding"
              maxLength={200}
              autoFocus
              data-testid="cb-title"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-description">Description</Label>
            <Textarea
              id="cb-description"
              value={form.description}
              onChange={(e) => update({ description: e.target.value })}
              placeholder="Reusable questions for new-hire onboarding sessions."
              maxLength={2000}
              data-testid="cb-description"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Default language</Label>
              <Select value={form.default_language} onValueChange={(v) => update({ default_language: v })}>
                <SelectTrigger data-testid="cb-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ar">Arabic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cb-client">Client tag (optional)</Label>
              <Input
                id="cb-client"
                value={form.client_id}
                onChange={(e) => update({ client_id: e.target.value })}
                placeholder="acme-corp"
                maxLength={100}
                data-testid="cb-client"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cb-tags">Tags (comma-separated)</Label>
            <Input
              id="cb-tags"
              value={form.tags}
              onChange={(e) => update({ tags: e.target.value })}
              placeholder="onboarding, tier-1, soft-skills"
              data-testid="cb-tags"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="cb-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave || saving} data-testid="cb-save">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Create bank
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
